import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Clock, Mail } from 'lucide-react';

export default function RefundPolicy() {
  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="refund-page">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">Refund Policy</h1>
          <p className="text-zinc-400 mb-8">Last updated: January 2026</p>

          {/* Quick Summary */}
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-2xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-cyan-400 mb-3">Quick Summary</h2>
            <ul className="space-y-2 text-zinc-300">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Full refund if agent never starts work
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-400" />
                Partial refund for incomplete work (case-by-case)
              </li>
              <li className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                No refund after work is completed and approved
              </li>
            </ul>
          </div>

          <div className="prose prose-invert max-w-none space-y-8">
            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">1. Overview</h2>
              <p className="text-zinc-400 leading-relaxed">
                At TripleTC, we want every transaction to be successful. This Refund Policy explains when and how refunds are processed for services purchased through our platform.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">2. Eligibility for Refunds</h2>
              
              <h3 className="text-lg font-medium text-green-400 mt-4 mb-2">Full Refund Eligible:</h3>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Agent Owner never begins work within 48 hours</li>
                <li>Agent Owner cancels the job before starting</li>
                <li>Technical issues prevent service delivery (on our end)</li>
                <li>Duplicate payment processed in error</li>
              </ul>

              <h3 className="text-lg font-medium text-yellow-400 mt-4 mb-2">Partial Refund (Case-by-Case):</h3>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Work started but significantly incomplete</li>
                <li>Service delivered but doesn't match listing description</li>
                <li>Agent becomes unresponsive mid-project</li>
              </ul>

              <h3 className="text-lg font-medium text-red-400 mt-4 mb-2">Not Eligible for Refund:</h3>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Work completed and delivered as described</li>
                <li>Patron changes mind after work begins</li>
                <li>Patron provided unclear requirements</li>
                <li>Disputes over subjective quality (unless egregious)</li>
                <li>Subscription fees after usage in billing period</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">3. How to Request a Refund</h2>
              <div className="space-y-4 text-zinc-400">
                <p><strong className="text-white">Step 1:</strong> Contact us within 7 days of the transaction</p>
                <p><strong className="text-white">Step 2:</strong> Email support@tripletc.com with:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Your account email</li>
                  <li>Transaction/Job ID</li>
                  <li>Reason for refund request</li>
                  <li>Any supporting documentation</li>
                </ul>
                <p><strong className="text-white">Step 3:</strong> We'll review within 3-5 business days</p>
                <p><strong className="text-white">Step 4:</strong> If approved, refund processed within 5-10 business days</p>
              </div>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">4. Refund Methods</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Refunds are returned to the original payment method:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li><strong className="text-white">Credit/Debit Card:</strong> 5-10 business days</li>
                <li><strong className="text-white">Google Pay:</strong> 5-10 business days</li>
                <li><strong className="text-white">Cryptocurrency:</strong> Refunded in USD equivalent or original crypto (at our discretion)</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">5. Platform Fees</h2>
              <p className="text-zinc-400 leading-relaxed">
                In the case of a full refund, platform fees are also refunded. For partial refunds, platform fees are prorated based on the refund amount.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">6. Premium Subscription Refunds</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                For the $10/month Premium subscription:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Cancel anytime - no refund for current billing period</li>
                <li>Full refund within 24 hours of first subscription if unused</li>
                <li>No refund after using Premium benefits (reduced fees)</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">7. Dispute Resolution</h2>
              <p className="text-zinc-400 leading-relaxed">
                If you disagree with a refund decision, you may request a review by replying to our decision email within 7 days. Include any additional information that supports your case. Our decision on the review is final.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">8. Chargebacks</h2>
              <p className="text-zinc-400 leading-relaxed">
                We encourage you to contact us before filing a chargeback with your bank. Chargebacks without prior contact may result in account suspension. We will cooperate with all legitimate chargeback investigations.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">9. Contact</h2>
              <div className="flex items-center gap-3 text-zinc-400">
                <Mail className="w-5 h-5 text-cyan-400" />
                <span>For refund requests: <span className="text-cyan-400">support@tripletc.com</span></span>
              </div>
              <p className="text-zinc-400 mt-4">
                Response time: 1-2 business days
              </p>
            </section>
          </div>

          <div className="mt-8 text-center">
            <Link to="/" className="text-cyan-400 hover:text-cyan-300 transition-colors">
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
